import { FC, useEffect, useRef, useState } from 'react';

import { isEqual, uniq } from 'lodash';

import { useSnapEditorSdk } from '@features/spark-snaps/hooks/useSnapEditorSdk';
import { useSnapViewQueue } from '@features/spark-snaps/hooks/useSnapViewQueue';

import { AddCircleOutline } from '@components/icons';
import ClickableArea from '@components/layout/ClickableArea';
import Paper from '@components/layout/Paper';
import toast from '@components/toast';

import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';

import SparkSnapItem from '../SparkSnapItem';
import CreateEditSnapDrawer from './CreateEditSnapDrawer';

import './SparkSnapsPanel.scss';

interface AddSparkSnapButtonProps {
    onClick: () => void;
}
const AddSparkSnapButton: FC<AddSparkSnapButtonProps> = ({ onClick }) => {
    return (
        <div className="spark-snap-item spark-snap-item_add-button">
            <ClickableArea data-testid="add-snap-button" onClick={onClick}>
                <div className="spark-snap-item_add-icon">
                    <div>
                        <AddCircleOutline />
                    </div>
                </div>
                <span className="spark-snap-item_add-label">Add</span>
            </ClickableArea>
        </div>
    );
};

interface SparkSnapsPanelProps {
    sparkId: string;
    accountId: string;
    initialSnapIds?: number[];
    onChange?: (params: {
        accountId: string;
        storifymeAccountId: string;
        snapIds: number[];
    }) => void;
}

const SparkSnapsPanel: FC<SparkSnapsPanelProps> = ({
    sparkId,
    accountId,
    initialSnapIds = [],
    onChange,
}) => {
    const [snapIds, setSnapIds] = useState(initialSnapIds);
    const [drawerIsVisible, setDrawerIsVisible] = useState(false);
    const [drawerSnapId, setDrawerSnapId] = useState<number>();
    const { handleViewSnap, SnapViewer } = useSnapViewQueue({ accountId, snapIds, sparkId });
    const { account: userAccount } = useSparkplugAccount();
    const accountCanModifySnaps = accountId === userAccount?._id && userAccount?.snapsEnabled;
    // check if the account can modify snaps for this spark or spark has existing snaps
    const canView = accountCanModifySnaps || initialSnapIds.length > 0;
    if (!canView) {
        return <></>;
    }

    /**
     * StorifyMe has a bug where it sends duplicate events.
     * By capturing the previousSnapIds we ensure that we only run onChange when necessary
     */
    const previousSnapIds = useRef<number[]>(initialSnapIds);

    /**
     * Because the snap editor "onSnapSaved" event is used for both creating new snaps
     * and updating existing snaps, we need to keep track of whether the snap editor
     * is being used to create a new snap or update an existing one. If we are editing
     * an existing snap, we don't want to update the snapIds state
     */
    const isCreatingNewSnapInEditor = useRef(false);
    const handleSnapEditorSaved = async ({ snapId }: { snapId: number }) => {
        if (snapId && isCreatingNewSnapInEditor.current) {
            setSnapIds((prevSnapIds) => {
                previousSnapIds.current = prevSnapIds;
                return uniq([...prevSnapIds, snapId]);
            });
        } else {
            toast.success('Snap Saved!');
        }
    };

    const { openSnapEditor, accountSnapSdkCredentials } = useSnapEditorSdk({
        accountId,
        onSnapSaved: handleSnapEditorSaved,
    });

    const handleRemoveSnap = async (id: number) => {
        setDrawerIsVisible(false);
        setSnapIds((prevSnapIds) => {
            previousSnapIds.current = prevSnapIds;
            return prevSnapIds.filter((snapId) => snapId !== id);
        });
    };

    const handleEditSnap = (snapId: number) => {
        setDrawerIsVisible(true);
        setDrawerSnapId(snapId);
    };

    const handleAddSnap = ({ snapName }: { snapName: string }) => {
        isCreatingNewSnapInEditor.current = true;
        setDrawerIsVisible(false);
        openSnapEditor({
            name: snapName, // Name of the new snap design
        });
    };

    const handleOpenSnapInEditor = (id: number) => {
        isCreatingNewSnapInEditor.current = false;
        setDrawerIsVisible(false);
        openSnapEditor({
            snapId: id,
        });
    };

    /**
     * If the snapIds change, we need to call the onChange callback
     */
    useEffect(() => {
        if (!isEqual(previousSnapIds.current, snapIds)) {
            onChange?.({
                accountId,
                storifymeAccountId: accountSnapSdkCredentials?.accountId ?? '',
                snapIds,
            });
        }
    }, [snapIds, accountSnapSdkCredentials?.accountId]);

    return (
        <>
            <Paper className="spark-snaps-panel">
                <Paper.Title>Spark Snaps</Paper.Title>
                <div className="spark-snaps">
                    {snapIds.map((snapId) => (
                        <SparkSnapItem
                            key={snapId}
                            accountId={accountId}
                            snapId={snapId}
                            onEdit={handleEditSnap}
                            canEdit={accountCanModifySnaps}
                            onView={handleViewSnap}
                        />
                    ))}
                    {accountCanModifySnaps && (
                        <AddSparkSnapButton
                            onClick={() => {
                                setDrawerIsVisible(true);
                                setDrawerSnapId(undefined);
                            }}
                        />
                    )}
                </div>
                <SnapViewer />
            </Paper>

            {drawerSnapId ? (
                <CreateEditSnapDrawer
                    isVisible={drawerIsVisible}
                    accountId={accountId}
                    snapId={drawerSnapId}
                    onClose={() => setDrawerIsVisible(false)}
                    onRemove={handleRemoveSnap}
                    onOpenInEditor={handleOpenSnapInEditor}
                />
            ) : (
                <CreateEditSnapDrawer
                    isVisible={drawerIsVisible}
                    onClose={() => setDrawerIsVisible(false)}
                    onAdd={handleAddSnap}
                    onOpenInEditor={handleOpenSnapInEditor}
                />
            )}
        </>
    );
};

export default SparkSnapsPanel;
